import { Observable } from "rxjs";
import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { Fiat } from "@models";
import { Router } from "@angular/router";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { ModalService } from "@core-services/modal.service";
import { TradeTypeDecisionModalComponent } from "../../trade/modals/trade-type-decision-modal/trade-type-decision-modal.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PortfolioFacade } from "@modules/portfolio/domain/portfolio.facade";
import { filter } from "rxjs/operators";
import { PORTFOLIO_MAX_ITEMS_TO_DISPLAY } from "@helpers/constants";
import { PathResolverService } from "@main-services/path-resolver.service";
import { BpOption } from "@models/ui-components/bp-option";
import { portfolioTimeFilter } from "@modules/portfolio/domain/portfolio.constants";
import { SegmentService } from "@main-services/segment.service";
import { PortfolioAssetProjection, PortfolioData } from "@modules/portfolio/domain/entities/portfolio-projection.model";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { Timespan } from "@helpers/const/timespan";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "portfolio-widget",
	templateUrl: "./portfolio-widget.component.html",
	styleUrls: ["./portfolio-widget.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class PortfolioWidgetComponent implements OnInit {
	public widgetVM$: Observable<PortfolioWidgetViewModel> = this.portfolioFacade.getPortfolioWidgetValues$();
	public defaultFiat: Fiat = this.portfolioFacade.getDefaultFiat();
	public time: BpOption = portfolioTimeFilter[0]; // Day by default
	public maxItemsShown = PORTFOLIO_MAX_ITEMS_TO_DISPLAY;

	public constructor(
		private readonly portfolioFacade: PortfolioFacade,
		private readonly modalService: ModalService,
		private readonly router: Router,
		private readonly fiatWalletService: FiatWalletService,
		private readonly pathResolverService: PathResolverService,
		private readonly segmentService: SegmentService,
		private readonly rudderstackService: RudderstackService
	) {}

	public ngOnInit(): void {
		// Initial fetch of portfolio data is done in the home component
		// Re-fetch portfolio data on prices or wallet updates
		this.portfolioFacade
			.getPriceUpdates$()
			.pipe(
				untilDestroyed(this),
				filter((_) => !document.hidden) // Drop any requests if the portfolio tab is not active
			)
			.subscribe(() => {
				this.updateData(this.time.value);
			});
	}

	public updateData(timespan): void {
		return !!timespan && this.portfolioFacade.fetchPortfolioOverviewAndPerformance(timespan);
	}

	public trade(): void {
		this.modalService.create(TradeTypeDecisionModalComponent, {}, { showCloseButton: false }).present();
	}

	public openAssetDetail(id: string): void {
		this.router.navigateByUrl(this.pathResolverService.assetDetailsById(id));
	}

	public goToWalletHome(): void {
		this.router.navigateByUrl("/portfolio");
	}

	public goToProfile(): void {
		this.router.navigateByUrl("/my-account");
	}

	public goToFiatCurrency(): void {
		const availableWallets = this.fiatWalletService.getNonEmptyWallets();
		if (availableWallets.length === 1) {
			this.router.navigateByUrl("/fiat-wallet/" + availableWallets[0].getId());
		} else {
			this.router.navigateByUrl("/portfolio?type=fiat");
		}
	}

	public updateTime(val: BpOption<string, Timespan>): void {
		if (val !== this.time) {
			this.time = val;
			this.updateData(this.time.value);
		}
		this.segmentService.track({
			name: "Portfolio Widget Chart Time Filter Clicked",
			properties: { time_filter_selected: val.value }
		});
		this.rudderstackService.track({
			name: "Portfolio Widget Chart Time Filter Clicked",
			properties: { time_filter_selected: val.value }
		});
	}

	public isUnverified(): boolean {
		return this.portfolioFacade.isUnverified();
	}
}

export interface PortfolioWidgetViewModel {
	assets: PortfolioAssetProjection[];
	portfolioData?: PortfolioData;
	fiatSum: number;
	assetCount: number;
	isLoading: boolean;
	apiError: boolean;
	profile: ProfileModel;
	language: string;
	timespanOptions: BpOption<string, Timespan>[];
	isHidePortfolioValueActive: boolean;
}
